import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/cn'

import { IconButton } from './Button'
import { ToastContext, type ToastApi, type ToastOptions } from './useToast'

type ToastItem = ToastOptions & { id: string }

const TONES = {
  neutral: 'border-border',
  accent: 'border-accent/40',
  danger: 'border-danger/50',
} as const

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())
  const counter = useRef(0)

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id)
    if (timer) clearTimeout(timer)
    timers.current.delete(id)
    setItems((list) => list.filter((t) => t.id !== id))
  }, [])

  const show = useCallback(
    (options: ToastOptions) => {
      counter.current += 1
      const id = options.id ?? `toast-${counter.current}`
      setItems((list) => [...list.filter((t) => t.id !== id), { ...options, id }].slice(-3))
      const previous = timers.current.get(id)
      if (previous) clearTimeout(previous)
      const duration = options.duration === undefined ? 4000 : options.duration
      if (duration !== null) timers.current.set(id, setTimeout(() => dismiss(id), duration))
      return id
    },
    [dismiss],
  )

  useEffect(() => {
    const map = timers.current
    return () => map.forEach((timer) => clearTimeout(timer))
  }, [])

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          aria-live="polite"
          className={cn(
            'pointer-events-none fixed inset-x-0 z-[60] flex flex-col items-center gap-2 px-3',
            'bottom-[calc(4.75rem+env(safe-area-inset-bottom))] lg:inset-x-auto lg:bottom-5 lg:right-5 lg:items-end',
          )}
        >
          {items.map((item) => (
            <div
              key={item.id}
              role={item.tone === 'danger' ? 'alert' : 'status'}
              className={cn(
                'pointer-events-auto flex w-full max-w-md items-center gap-2 rounded-xl border bg-surface py-1.5 pl-4 pr-1.5 text-sm text-fg shadow-lg',
                TONES[item.tone ?? 'neutral'],
              )}
            >
              <span className="min-w-0 flex-1 py-1.5">{item.message}</span>
              {item.action ? (
                <button
                  type="button"
                  className="min-h-11 shrink-0 rounded-lg px-3 font-semibold text-accent hover:bg-surface-2 lg:min-h-9"
                  onClick={() => {
                    item.action?.onClick()
                    dismiss(item.id)
                  }}
                >
                  {item.action.label}
                </button>
              ) : null}
              <IconButton icon="close" label="Закрыть" size="sm" onClick={() => dismiss(item.id)} />
            </div>
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}
